import React, { useState } from 'react';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';
import ColourGrid from '../ColourGrid';
import FontPreview from '../FontPreview';
import SelectionCard from '../SelectionCard';

function Step2DesignPreferences() {
  const { formData, updateFormData } = useForm();
  const stepData = formData.step2;
  const businessName = formData.step1.businessName || 'YOURCOMPANY';
  const businessNameFirst = businessName.split(' ')[0];

  const [selectedColours, setSelectedColours] = useState([
    stepData.primaryColour || '',
    stepData.secondaryColour || ''
  ].filter(Boolean));

  const handleColourSelection = (colour) => {
    let newSelectedColours = [...selectedColours];
    
    if (newSelectedColours.includes(colour)) {
      newSelectedColours = newSelectedColours.filter(c => c !== colour);
    } else {
      if (newSelectedColours.length < 2) {
        newSelectedColours.push(colour);
      } else {
        alert('You must first unselect one colour or click Clear selection');
        return;
      }
    }
    
    setSelectedColours(newSelectedColours);
    updateFormData('step2', {
      primaryColour: newSelectedColours[0] || '',
      secondaryColour: newSelectedColours[1] || ''
    });
  };

  const clearColours = () => {
    setSelectedColours([]);
    updateFormData('step2', { primaryColour: '', secondaryColour: '' });
  };

  const handleHexInput = (value, type) => {
    if (value.startsWith('#') && /^#[0-9A-F]{6}$/i.test(value)) {
      const newSelectedColours = [...selectedColours];
      if (type === 'primary') {
        newSelectedColours[0] = value;
      } else {
        newSelectedColours[1] = value;
      }
      setSelectedColours(newSelectedColours.filter(Boolean));
      updateFormData('step2', {
        primaryColour: newSelectedColours[0] || '',
        secondaryColour: newSelectedColours[1] || ''
      });
    }
  };

  const designStyles = [
    { id: 'Modern-minimalist', title: 'Modern & minimalist' },
    { id: 'Bold-vibrant', title: 'Bold & vibrant' },
    { id: 'Corporate-professional', title: 'Corporate & professional' },
    { id: 'Creative-artistic', title: 'Creative & artistic' },
    { id: 'Elegant-luxurious', title: 'Elegant & luxurious' },
    { id: 'Fun-playful', title: 'Fun & playful' }
  ];

  const handleStyleToggle = (styleId) => {
    const currentStyles = stepData.designStyle || [];
    const updatedStyles = currentStyles.includes(styleId)
      ? currentStyles.filter(id => id !== styleId)
      : [...currentStyles, styleId];
    
    updateFormData('step2', { designStyle: updatedStyles });
  };

  return (
    <StepLayout
      title="Let's bring your vision to life! 🎨"
      stepNumber={2}
      totalSteps={7}
      nextButtonText="Bring in my brand"
      nextStep={3}
      prevStep={1}
    >
      <div className="p-4 bg-[#FFF7ED] border-l-4 border-[#F59E0B] rounded-lg mb-8">
        <p className="text-[#92400D] leading-relaxed">
          Now comes the exciting part where we explore your visual style together. We will discover the colours that that reflect your brand's essence, the font that speaks your language, and the overall aesthetic that feels authentically you. Don't worry if you are not sure about everything yet, we are here to guide you through each choice.
        </p>
      </div>

      <div className="space-y-16">
        {/* Colour Selection */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Choose your branding colours</h3>
          <div className="mb-6 p-4 bg-[#F9FAFB] border-l-4 border-[#FF5841] rounded-lg">
            <p className="text-[#383E47] mb-3">
              Colour choices shape the visual style and user experience of a website, influencing how visitors perceive your brand and engage with your content. Each colour conveys a distinct message: blue evokes calm and trust, red signals energy or urgency, and green represents growth and freshness.
            </p>
          </div>
          
          <p className="text-base text-gray-700 mb-4">
            Choose up to 2 primary colours that represent your brand. If you cannot find your brand colours, enter hex codes below or use the colour wheel to select them.
          </p>

          <div className="p-4 bg-[#E5F9F9] border-l-4 border-[#008080] rounded-lg mb-4">
            <div className="flex justify-between items-center">
              <span className="text-[#008080]">
                Selected: {selectedColours.length} of 2 colours
              </span>
              {selectedColours.length > 0 && (
                <button
                  onClick={clearColours}
                  className="px-3 py-1 bg-[#ED4145] text-white rounded-md hover:bg-[#D6393D] transition-colors duration-200"
                >
                  Clear selection
                </button>
              )}
            </div>
          </div>

          <ColourGrid
            selectedColours={selectedColours}
            onColourSelect={handleColourSelection}
          />

          {/* Hex Input Section */}
          <div className="mt-8 space-y-4">
            <h4 className="font-medium text-gray-900 text-lg mt-12">🌈 Alternatively choose from the colour wheel or enter hex codes below</h4>
            
            {/* Colour Wheel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-base text-gray-600 mb-2">Click to pick a primary colour or enter its hex code:</label>
                <input
                  type="color"
                  value={stepData.primaryColour || '#000000'}
                  onChange={(e) => handleHexInput(e.target.value, 'primary')}
                  className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-base text-gray-600 mb-2">Click to pick a secondary colour or enter its hex code:</label>
                <input
                  type="color"
                  value={stepData.secondaryColour || '#000000'}
                  onChange={(e) => handleHexInput(e.target.value, 'secondary')}
                  className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Colour Preview */}
          {selectedColours.length > 0 && (
            <div className="mt-16">
              <h4 className="font-medium text-gray-900 mb-4 text-18px">Preview your colours</h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {/* Header */}
                <div 
                  className="px-6 py-4 flex justify-between items-center"
                  style={{ backgroundColor: selectedColours[0] || '#FF5841' }}
                >
                  <div className="text-white font-bold">
                    {businessNameFirst.toUpperCase()} LOGO
                  </div>
                  <div className="hamburger-menu">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h2 className="text-2xl font-bold mb-4" style={{ color: selectedColours[0] || '#FF5841' }}>
                    Welcome to {businessNameFirst}
                  </h2>
                  <p className="text-gray-700 mb-6">
                    At {businessNameFirst}, we believe in creating value and exceeding expectations. With our dedicated team and proven expertise, we consistently strive to bring you solutions that empower your success.
                  </p>
                  
                  <h3 className="text-lg font-semibold mb-4" style={{ color: selectedColours[0] || '#FF5841' }}>
                    What We Offer
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div 
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: `${selectedColours[0] || '#FF5841'}10` }}
                    >
                      <h4 className="font-semibold mb-2" style={{ color: selectedColours[0] || '#FF5841' }}>
                        Quality & Reliability
                      </h4>
                      <ul className="text-15px text-gray-600 space-y-1">
                        <li>• Trusted products crafted with care</li>
                        <li>• Dedicated customer support team</li>
                        <li>• Commitment to timely delivery</li>
                      </ul>
                    </div>
                    
                    {selectedColours[1] && (
                      <div 
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: `${selectedColours[1]}10` }}
                      >
                        <h4 className="font-semibold mb-2" style={{ color: selectedColours[1] }}>
                          Innovation & Growth
                        </h4>
                        <ul className="text-15px text-gray-600 space-y-1">
                          <li>• Innovative solutions tailored for you</li>
                          <li>• Focus on sustainable growth</li>
                          <li>• Partnering for your long-term success</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-4">
                    <button 
                      className="px-6 py-2 text-white rounded-lg font-medium"
                      style={{ backgroundColor: selectedColours[0] || '#FF5841' }}
                    >
                      Contact Us
                    </button>
                    {selectedColours[1] && (
                      <button 
                        className="px-6 py-2 text-white rounded-lg font-medium"
                        style={{ backgroundColor: selectedColours[1] }}
                      >
                        Learn More
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Colour Comments */}
          <div className="mt-6">
            <label className="block text-base font-medium text-gray-900 mb-2">
              Additional comments about colours (optional)
            </label>
            <textarea
              value={stepData.colourComments || ''}
              onChange={(e) => updateFormData('step2', { colourComments: e.target.value })}
              placeholder="Enter any specific requirements, colour meanings, or preferences for your brand colours..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Typography */}
        <div className="mt-8">
          <h3 className="text-xl font-medium text-gray-900 mb-4">Typography</h3>
          <div className="typography-section rounded-lg">
            <p className="mb-2">
              Typography is how your website's text looks and feels: it directly impacts readability, style, and the impression your brand gives.
            </p>
            <p className="text-15">
              For a wider range of fonts, visit <a href="https://fonts.google.com/" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline">Google Fonts</a>
            </p>
          </div>

          <div className="mt-12">
          <FontPreview
            selectedFont={stepData.selectedFont}
            onFontSelect={(font) => updateFormData('step2', { selectedFont: font })}
            colours={selectedColours}
            businessName={businessName}
          />
          </div>
          
          <div className="mt-6">
            <label className="block text-base font-medium text-gray-900 mb-2">
              Additional comments about typography (optional)
            </label>
            <textarea
              value={stepData.typographyComments || ''}
              onChange={(e) => updateFormData('step2', { typographyComments: e.target.value })}
              placeholder="Enter any specific typography preferences or requirements..."
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Design Style */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Design style preference</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {designStyles.map((style) => (
              <SelectionCard
                key={style.id}
                title={style.title}
                selected={(stepData.designStyle || []).includes(style.id)}
                onClick={() => handleStyleToggle(style.id)}
                className="text-center"
              />
            ))}
          </div>
        </div>

        {/* Design Inspiration */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Design inspiration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-base font-medium text-gray-900 mb-2">
                Inspiration website URLs
              </label>
              <textarea
                value={stepData.inspirationUrls || ''}
                onChange={(e) => updateFormData('step2', { inspirationUrls: e.target.value })}
                placeholder="Share links to websites you admire or that inspire your vision..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
              />
            </div>
            
            <div>
              <label className="block text-base font-medium text-gray-900 mb-2">
                Style preference descriptions
              </label>
              <textarea
                value={stepData.designReferences || ''}
                onChange={(e) => updateFormData('step2', { designReferences: e.target.value })}
                placeholder="Describe the visual style, mood, or specific design elements you would like to see..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}

export default Step2DesignPreferences;